import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { api, Venue, Court, Booking, centsToDollars } from '../api/client';
import { BusinessProfile, CourtConfig, Reservation, RefundPolicyConfig, SportType, CourtType } from '../types';
import { BOOKING_STATUS } from '../constants/checkout';
import { useAuth } from './AuthContext';
import { useLocation } from 'react-router-dom';

// ============================================
// Context Type
// ============================================

interface BusinessContextType {
  business: BusinessProfile | null;
  reservations: Reservation[];
  loading: boolean;
  error: string | null;
  needsVenueSelection: boolean;
  refreshData: () => Promise<void>;
  updateBusiness: (updates: Partial<BusinessProfile>) => void;
  addCourt: (court: CourtConfig) => Promise<void>;
  updateCourt: (courtId: string, updates: Partial<CourtConfig>) => Promise<void>;
  deleteCourt: (courtId: string) => Promise<void>;
  updateReservation: (reservationId: string, updates: Partial<Reservation>) => Promise<void>;
  updateVenuePricing: (indoorRateCents: number, outdoorRateCents: number) => Promise<void>;
  updateBookingWindowDays: (days: number) => Promise<void>;
  updateRefundPolicy: (policy: RefundPolicyConfig) => void;
  publishBusiness: () => Promise<void>;
  unpublishBusiness: () => Promise<void>;
  saveBusiness: () => Promise<void>;
}

const BusinessContext = createContext<BusinessContextType | undefined>(undefined);

export function shouldLoadFullBusinessData(pathname: string): boolean {
  return pathname !== '/audiences';
}

function requireTimezone(timezone: string | undefined, context: string): string {
  if (!timezone) {
    throw new Error(`Missing timezone for ${context}`);
  }
  return timezone;
}

// ============================================
// Data Mappers (API <-> Frontend)
// ============================================

function mapVenueToBusiness(venue: Venue, courts: Court[]): BusinessProfile {
  const config = venue.config;
  return {
    id: venue.id,
    ownerId: venue.id,
    businessName: venue.name,
    timezone: requireTimezone(venue.timezone, `venue ${venue.id}`),
    email: config.email || '',
    phone: config.phone || '',
    website: config.website || '',
    location: {
      address: config.address?.street || '',
      city: config.address?.city || '',
      state: config.address?.state || '',
      zipCode: config.address?.zip || '',
    },
    description: config.description || '',
    photos: (config.photos || []).map(p => ({
      id: p.id || `photo-${Date.now()}`,
      url: p.url,
      caption: p.caption || '',
      isMain: p.isMain || false,
    })),
    waiverPdf: (() => {
      const waiver = config.waiver_pdf as Record<string, unknown> | undefined;
      const url = typeof waiver?.url === 'string' ? (waiver.url as string).trim() : '';
      if (!url || waiver?.enabled === false) return null;
      return {
        enabled: true,
        url,
        fileName: (waiver?.file_name as string) || 'Waiver.pdf',
        sha256: typeof waiver?.sha256 === 'string' ? (waiver.sha256 as string) : null,
        uploadedAt: typeof waiver?.uploaded_at === 'string' ? (waiver.uploaded_at as string) : null,
      };
    })(),
    equipmentRental: (() => {
      const parsedPriceCents = Number(config.equipment_rental?.price_cents);
      return {
        enabled: config.equipment_rental?.enabled === true,
        label: config.equipment_rental?.label || 'Paddle + Ball Rental',
        priceCents: Number.isFinite(parsedPriceCents) ? Math.max(0, Math.round(parsedPriceCents)) : 0,
      };
    })(),
    indoorRateCents: venue.indoor_rate_cents ?? 3000,
    outdoorRateCents: venue.outdoor_rate_cents ?? 2000,
    courts: courts.map(mapCourtToConfig),
    pricing: [], // Pricing now managed via venue-level rates
    pricingSpecials: [],
    operatingHours: (config.operating_hours || []).map(h => ({
      dayOfWeek: h.dayOfWeek,
      openTime: h.open,
      closeTime: h.close,
      isClosed: h.isClosed,
    })),
    refundPolicy: config.refund_policy ? {
      type: config.refund_policy.type,
      fullRefundHours: config.refund_policy.fullRefundHours,
      partialRefundHours: config.refund_policy.partialRefundHours,
      partialRefundPercent: config.refund_policy.partialRefundPercent,
    } : {
      type: 'partial',
      fullRefundHours: 48,
      partialRefundHours: 24,
      partialRefundPercent: 50,
    },
    bookingWindowDays: typeof config.booking_window_days === 'number' ? config.booking_window_days : 14,
    isPublished: config.is_published || false,
    paymentRouting: venue.payment_routing,
    createdAt: venue.created_at,
    updatedAt: venue.updated_at,
  };
}

function mapCourtToConfig(court: Court): CourtConfig {
  // Handle photos - might be array, string, or undefined
  let photos: Array<{ url: string }> = [];
  if (Array.isArray(court.photos)) {
    photos = court.photos;
  } else if (typeof court.photos === 'string') {
    try {
      photos = JSON.parse(court.photos);
    } catch {
      photos = [];
    }
  }

  return {
    id: court.id,
    name: court.name,
    sportType: court.sport_type as SportType,
    courtType: court.court_type as CourtType,
    externalMappedName: court.mapped_external_name || '',
    syncedIntegrationType: court.mapped_integration_type || '',
    description: court.description || '',
    amenities: Array.isArray(court.amenities) ? court.amenities : [],
    images: photos.map(p => p.url),
    isActive: court.is_active !== false,
  };
}

function mapBookingToReservation(booking: Booking, timezone: string): Reservation {
  const startDate = new Date(booking.start_time);
  const endDate = new Date(booking.end_time);
  const durationMs = endDate.getTime() - startDate.getTime();
  const durationHours = durationMs / (1000 * 60 * 60);

  // Convert real UTC timestamps to venue-local HH:MM for display
  const startLocal = startDate.toLocaleTimeString('en-US', {
    timeZone: timezone, hour: '2-digit', minute: '2-digit', hour12: false,
  });
  const endLocal = endDate.toLocaleTimeString('en-US', {
    timeZone: timezone, hour: '2-digit', minute: '2-digit', hour12: false,
  });

  // Extract venue-local date (UTC date may differ from local date for late-night bookings)
  const localDate = startDate.toLocaleDateString('en-CA', { timeZone: timezone });

  return {
    id: booking.id,
    courtId: booking.court_id,
    confirmationCode: booking.confirmation_code,
    customerName: `${booking.customer_first_name || ''} ${booking.customer_last_name || ''}`.trim(),
    customerEmail: booking.customer_email || '',
    customerPhone: booking.customer_phone || '',
    date: localDate,
    startTime: startLocal,
    endTime: endLocal,
    durationHours,
    totalAmount: booking.payment_amount_cents ? centsToDollars(booking.payment_amount_cents) : 0,
    status: (booking.status as string) === 'cancelled' ? 'canceled' : booking.status,
    paymentFlow: booking.payment_flow,
    source: booking.source || 'app',
    notes: booking.notes,
    createdAt: booking.created_at,
    updatedAt: booking.updated_at,
    autoApprovalStatus: booking.checkout_metadata?.autoApproval?.status || null,
    autoCancellationStatus: booking.checkout_metadata?.autoCancellation?.status || null,
  };
}

function mapBusinessToVenueConfig(business: BusinessProfile): Venue['config'] {
  return {
    description: business.description,
    email: business.email,
    phone: business.phone,
    website: business.website,
    address: {
      street: business.location.address,
      city: business.location.city,
      state: business.location.state,
      zip: business.location.zipCode,
    },
    operating_hours: business.operatingHours?.map(h => ({
      dayOfWeek: h.dayOfWeek,
      open: h.openTime,
      close: h.closeTime,
      isClosed: h.isClosed,
    })),
    refund_policy: business.refundPolicy ? {
      type: business.refundPolicy.type,
      fullRefundHours: business.refundPolicy.fullRefundHours,
      partialRefundHours: business.refundPolicy.partialRefundHours,
      partialRefundPercent: business.refundPolicy.partialRefundPercent,
    } : undefined,
    waiver_pdf: business.waiverPdf ? {
      enabled: business.waiverPdf.enabled,
      url: business.waiverPdf.url,
      file_name: business.waiverPdf.fileName,
      ...(business.waiverPdf.sha256 ? { sha256: business.waiverPdf.sha256 } : {}),
      ...(business.waiverPdf.uploadedAt ? { uploaded_at: business.waiverPdf.uploadedAt } : {}),
    } : { enabled: false, url: null, file_name: null },
    equipment_rental: {
      enabled: business.equipmentRental?.enabled === true,
      label: business.equipmentRental?.label || 'Paddle + Ball Rental',
      price_cents: Number.isFinite(business.equipmentRental?.priceCents)
        ? Math.max(0, Math.round(Number(business.equipmentRental.priceCents)))
        : 0,
    },
    is_published: business.isPublished,
  };
}

// ============================================
// Provider
// ============================================

export function BusinessProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const [business, setBusiness] = useState<BusinessProfile | null>(null);
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [needsVenueSelection, setNeedsVenueSelection] = useState(false);

  const refreshData = useCallback(async () => {
    if (!isAuthenticated || !api.getToken()) {
      setLoading(false);
      return;
    }

    // The audience bootstrap owns everything needed by /audiences. Loading
    // courts and bookings here used most of the shared Cloudflare request
    // window before the audience screen made its own requests.
    if (!shouldLoadFullBusinessData(location.pathname)) {
      setLoading(false);
      setError(null);
      setNeedsVenueSelection(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setNeedsVenueSelection(false);

      const [venueRes, courts, bookings] = await Promise.all([
        api.getVenue(),
        api.getCourts(),
        api.getBookings(),
      ]);

      const venueTz = requireTimezone(venueRes.venue.timezone, `venue ${venueRes.venue.id}`);
      setBusiness(mapVenueToBusiness(venueRes.venue, courts));
      setReservations(bookings.map(b => mapBookingToReservation(b, venueTz)));
    } catch (err: any) {
      // Check if SUPERADMIN needs to select a venue
      const errMsg = err.message?.toLowerCase() || '';
      if (errMsg.includes('venue id required') || errMsg.includes('superadmin must specify venue') || errMsg.includes('needsvenueselection')) {
        setNeedsVenueSelection(true);
        setError(null);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load data');
      }
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, location.pathname]);

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  const updateBusiness = (updates: Partial<BusinessProfile>) => {
    if (business) {
      setBusiness({ ...business, ...updates, updatedAt: new Date().toISOString() });
    }
  };

  const addCourt = async (court: CourtConfig) => {
    try {
      const created = await api.createCourt({
        name: court.name,
        sportType: court.sportType,
        courtType: court.courtType,
        description: court.description,
        amenities: court.amenities,
      });

      if (business) {
        setBusiness({
          ...business,
          courts: [...business.courts, mapCourtToConfig(created)],
          updatedAt: new Date().toISOString(),
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create court');
      throw err;
    }
  };

  const updateCourt = async (courtId: string, updates: Partial<CourtConfig>) => {
    try {
      await api.updateCourt(courtId, {
        name: updates.name,
        sportType: updates.sportType,
        courtType: updates.courtType,
        description: updates.description,
        amenities: updates.amenities,
        isActive: updates.isActive,
      });

      if (business) {
        setBusiness({
          ...business,
          courts: business.courts.map(c => c.id === courtId ? { ...c, ...updates } : c),
          updatedAt: new Date().toISOString(),
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update court');
      throw err;
    }
  };

  const deleteCourt = async (courtId: string) => {
    try {
      await api.deleteCourt(courtId);

      if (business) {
        setBusiness({
          ...business,
          courts: business.courts.filter(c => c.id !== courtId),
          updatedAt: new Date().toISOString(),
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete court');
      throw err;
    }
  };

  const updateReservation = async (reservationId: string, updates: Partial<Reservation>) => {
    try {
      const existingReservation = reservations.find(r => r.id === reservationId);
      const isAwaitingDecision = existingReservation?.status === BOOKING_STATUS.PENDING_APPROVAL
        || existingReservation?.status === 'manual_booking_approval' // legacy, remove after 2026-08
        || existingReservation?.status === 'pending';

      const isApproveAction = updates.status === 'confirmed' || updates.status === 'approved';
      const isDeclineAction = updates.status === 'canceled' || updates.status === 'declined';

      if (isAwaitingDecision && isApproveAction) {
        const result = await api.approveReservation(reservationId);
        setReservations(reservations.map(r =>
          r.id === reservationId ? { ...r, status: result.reservation.status as Reservation['status'], updatedAt: new Date().toISOString() } : r
        ));
        return;
      }

      if (isAwaitingDecision && isDeclineAction) {
        const result = await api.declineReservation(reservationId);
        setReservations(reservations.map(r =>
          r.id === reservationId ? { ...r, status: result.reservation.status as Reservation['status'], updatedAt: new Date().toISOString() } : r
        ));
        return;
      }

      // Parse customer name if provided
      let customer;
      if (updates.customerName) {
        const [firstName, ...lastParts] = updates.customerName.split(' ');
        customer = {
          firstName,
          lastName: lastParts.join(' '),
          email: updates.customerEmail,
          phone: updates.customerPhone,
        };
      }

      await api.updateBooking(reservationId, {
        status: updates.status,
        notes: updates.notes !== undefined ? updates.notes : undefined,
        courtId: updates.courtId,
        date: updates.date,
        startTime: updates.startTime,
        endTime: updates.endTime,
        source: updates.source,
        customer,
      });

      setReservations(reservations.map(r =>
        r.id === reservationId ? { ...r, ...updates, updatedAt: new Date().toISOString() } : r
      ));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update reservation');
      throw err;
    }
  };

  const updateVenuePricing = async (indoorRateCents: number, outdoorRateCents: number) => {
    if (!business) return;
    try {
      await api.updateVenuePricing({ indoorRateCents, outdoorRateCents });
      setBusiness({ ...business, indoorRateCents, outdoorRateCents, updatedAt: new Date().toISOString() });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update pricing');
      throw err;
    }
  };

  const updateBookingWindowDays = async (days: number) => {
    if (!business) return;
    try {
      await api.updateVenue({ config: { booking_window_days: days } });
      setBusiness({ ...business, bookingWindowDays: days, updatedAt: new Date().toISOString() });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update booking window');
      throw err;
    }
  };

  const updateRefundPolicy = (policy: RefundPolicyConfig) => {
    if (business) {
      setBusiness({ ...business, refundPolicy: policy, updatedAt: new Date().toISOString() });
    }
  };

  const publishBusiness = async () => {
    if (!business) return;
    try {
      setLoading(true);
      await api.updateVenue({
        config: { ...mapBusinessToVenueConfig(business), is_published: true },
      });
      setBusiness({ ...business, isPublished: true, updatedAt: new Date().toISOString() });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to publish');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const unpublishBusiness = async () => {
    if (!business) return;
    try {
      setLoading(true);
      await api.updateVenue({
        config: { ...mapBusinessToVenueConfig(business), is_published: false },
      });
      setBusiness({ ...business, isPublished: false, updatedAt: new Date().toISOString() });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unpublish');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const saveBusiness = async () => {
    if (!business) return;
    try {
      await api.updateVenue({
        name: business.businessName,
        config: mapBusinessToVenueConfig(business),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
      throw err;
    }
  };

  return (
    <BusinessContext.Provider
      value={{
    business,
    reservations,
    loading,
    error,
    needsVenueSelection,
    refreshData,
    updateBusiness,
    addCourt,
    updateCourt,
    deleteCourt,
    updateReservation,
        updateVenuePricing,
        updateBookingWindowDays,
        updateRefundPolicy,
        publishBusiness,
        unpublishBusiness,
        saveBusiness,
      }}
    >
      {children}
    </BusinessContext.Provider>
  );
}

export function useBusiness() {
  const context = useContext(BusinessContext);
  if (context === undefined) {
    throw new Error('useBusiness must be used within a BusinessProvider');
  }
  return context;
}
